import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Organization } from '../../dto/Organization';
import { OrganizationWithAccesses } from '../../../../core/component/hornavbar/dto/InitDataDTO';
import { AuthService } from '../../../auth/service/auth.service';
import { HorNavbarService } from '../../../../core/service/hor-navbar.service';
import { Router } from '@angular/router';
import { PortfolioPerformanceService } from '../../../pp/service/portfolio-performance.service';
import { ChangeRouteAndSwitchFund } from '../../../../core/component/hornavbar/horizontal-nav-bar.component';
import { OrganizationPageService } from './organization-page.service';
import { cloneDeep } from 'lodash';

@Injectable({
  providedIn: 'root',
})
export class OrganizationsService {
  private allOrgsWithAccesses: OrganizationWithAccesses[];
  private selectedOrganizationAccesses: OrganizationAccesses;

  private _all = new BehaviorSubject<Organization[]>(undefined);
  private _selected = new BehaviorSubject<Organization>(undefined);
  private _selectedWithAccesses = new BehaviorSubject<OrganizationWithAccesses>(undefined);
  private _emulateSwitchFund = new BehaviorSubject<ChangeRouteAndSwitchFund>(undefined);
  private _showCompanyCrudModal = new BehaviorSubject<boolean>(false);

  constructor(
    private authService: AuthService,
    private navbarService: HorNavbarService,
    private orgPageService: OrganizationPageService,
    private router: Router,
    private ppService: PortfolioPerformanceService) {
  }

  setAll(organizations: OrganizationWithAccesses[]): void {
    this.allOrgsWithAccesses = organizations;
    const organizationsWithoutRoles = organizations.map((organization) => organization.organization);
    this._all.next(organizationsWithoutRoles);
  }

  setSelected(org: Organization): void {
    const currentData = this._selected.getValue();
    if (currentData && org === currentData) {
      return;
    }
    this.navbarService.resetPeriods();
    this.orgPageService.setFpOverview(undefined); // organization page should reset their fps, so prev fps are not used by org change.
    const orgWithAccesses = this.allOrgsWithAccesses.find((o) => o.organization.id === org.id);
    orgWithAccesses.organization.selectedFund = org.selectedFund;
    this.selectedOrganizationAccesses = {
      isOrganizationAdmin: orgWithAccesses.userAccesses.isOrgAdmin,
      isSuperAdmin: this.authService.isAdminSuper,
    };
    this.authService.setFmsAccessInsideOrganization(orgWithAccesses.userAccesses.fmsAccess);

    if (!org.selectedFund && org.funds.length) {
      org.selectedFund = org.funds[0];
    }
    this.router.navigate([], { queryParams: { f: org.selectedFund?.uuid, o: org.uuid } });
    this.ppService.setSelectedCompany(null);
    this._selected.next(org);
    this._selectedWithAccesses.next(orgWithAccesses);
  }

  setEmulateSwitchFund(data: ChangeRouteAndSwitchFund): void {
    this._emulateSwitchFund.next(data);
  }

  get all$(): BehaviorSubject<Organization[]> {
    return this._all;
  }

  get selected$(): BehaviorSubject<Organization> {
    return this._selected;
  }

  get selectedWithAccesses$(): BehaviorSubject<OrganizationWithAccesses> {
    return this._selectedWithAccesses;
  }

  get emulateSwitchFundState$(): Observable<ChangeRouteAndSwitchFund> {
    return this._emulateSwitchFund.asObservable();
  }

  get allOrgsWithAccess(): OrganizationWithAccesses[] {
    return this.allOrgsWithAccesses;
  }

  get selectedOrgAccesses(): OrganizationAccesses {
    return cloneDeep(this.selectedOrganizationAccesses);
  }

  get showCompanyCrudModal(): Observable<boolean> {
    return this._showCompanyCrudModal.asObservable();
  }

  setShowCompanyCrudModal(value: boolean): void {
    this._showCompanyCrudModal.next(value);
  }

  resetAllData(): void {
    this._all.next(null);
    this._selected.next(null);
    this._selectedWithAccesses.next(null);
    this._emulateSwitchFund.next(null);
  }
}

export class OrganizationAccesses {
  isSuperAdmin: boolean;
  isOrganizationAdmin: boolean;
}
